import { useContext, useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import AuthContext from "../../context/AuthContext";
import Fetch from "../../services/Fetch";
import { useConstants } from "../../hooks/UseConstants";
import PageContent from "../../components/PageContent";
import AdminPageHero from "../../components/AdminPageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import { parsePaginatedResponse } from "../../utils/parsePaginatedResponse";
import { getNotificationText } from "../../helper/notificationText";
import useSnackBar from "../../hooks/UseSnackBar";
import SnackbarAlert from "../../components/SnackBar";

function Notifications() {
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const { wait } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [broadcast, setBroadcast] = useState({ title_ar: '', title_en: '', description_ar: '', description_en: '' });
    const [sending, setSending] = useState(false);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();

    const loadHistory = async () => {
        setLoading(true);
        const result = await Fetch(`${host}/notification/history?per_page=50`, 'GET');
        const { items: rows, pagination } = parsePaginatedResponse(result);
        setItems(rows);
        setTotal(pagination.total ?? rows.length);
        setLoading(false);
    };

    useEffect(() => {
        if (wait) return;
        loadHistory();
    }, [host, wait]);

    const sendBroadcast = async () => {
        setSending(true);
        const result = await Fetch(`${host}/admin/notifications/broadcast`, 'POST', broadcast);
        if (result.status === 200) {
            setSnackBar('success', isAr ? 'تم إرسال الإشعار للجميع' : 'Broadcast sent');
            setBroadcast({ title_ar: '', title_en: '', description_ar: '', description_en: '' });
            loadHistory();
        } else {
            setSnackBar('error', result.data?.errors?.[0] || result.data?.message || 'Error');
        }
        setSending(false);
    };

    return (
        <PageContent wait={wait} loading={loading}>
            <AdminPageHero />
            <DashboardPanelCard className="mx-0 mb-4" title={isAr ? 'إرسال إشعار عام' : 'Broadcast notification'}>
                <Box className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <TextField label={isAr ? 'العنوان (عربي)' : 'Title (AR)'} value={broadcast.title_ar} onChange={(e) => setBroadcast({ ...broadcast, title_ar: e.target.value })} />
                    <TextField label={isAr ? 'العنوان (إنجليزي)' : 'Title (EN)'} value={broadcast.title_en} onChange={(e) => setBroadcast({ ...broadcast, title_en: e.target.value })} />
                    <TextField multiline rows={2} label={isAr ? 'النص (عربي)' : 'Body (AR)'} value={broadcast.description_ar} onChange={(e) => setBroadcast({ ...broadcast, description_ar: e.target.value })} />
                    <TextField multiline rows={2} label={isAr ? 'النص (إنجليزي)' : 'Body (EN)'} value={broadcast.description_en} onChange={(e) => setBroadcast({ ...broadcast, description_en: e.target.value })} />
                </Box>
                <Button variant="contained" sx={{ mt: 2 }} onClick={sendBroadcast} disabled={sending}>
                    {isAr ? 'إرسال للجميع' : 'Send to all users'}
                </Button>
            </DashboardPanelCard>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isAr ? `إجمالي الإشعارات: ${total}` : `Total notifications: ${total}`}
            </Typography>
            {items.length === 0 ? (
                <Typography color="text.secondary" className="text-center py-12">
                    {isAr ? 'لا توجد إشعارات' : 'No notifications'}
                </Typography>
            ) : (
                <DashboardPanelCard className="mx-0">
                    {items.map((n, i) => {
                        const { title, body } = getNotificationText(n, isAr);
                        return (
                            <ScrollReveal key={n.id || i} delay={Math.min(i * 40, 360)} direction="up">
                                <Box sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                                    <Typography variant="body2" fontWeight={600}>{title}</Typography>
                                    {body ? (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {body}
                                        </Typography>
                                    ) : null}
                                    {n.created_at ? (
                                        <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 0.5 }}>
                                            {n.created_at}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </ScrollReveal>
                        );
                    })}
                </DashboardPanelCard>
            )}
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </PageContent>
    );
}

export default Notifications;
